import { SegmentEffects } from '@opengov/app-core/analytics';
import { entityEffects, getEntity } from '@opengov/app-core/entity';
import { getSessionEntitySubdomain } from '@opengov/app-core/session/selectors';
import { claimsEffects } from '@opengov/app-core/claims/effects';
import { ReportingConfig } from '@opengov/app-config-reporting';
import { routerActionCreators } from '@opengov/compose-app-router/state';
import { IComposeAppRendererParams } from '@opengov/compose-app/renderApp';
import { LoaderFunction, NavigateFunction, redirect } from 'react-router-dom';
import { getLogger } from '../../application/instrument/useLogger';
import { isEmbeddedFromUrl } from './paths';

type RouteLoader = (store: IComposeAppRendererParams<object>['store']) => LoaderFunction<unknown>;

// Investigation aid for entity resolution: logs only on a redirect or a failed
// requested-entity fetch (never on the happy path) so we can distinguish an unreadable
// entity falling back to the home entity from a stale-token 403/404 on the entity API.
// In PSP builds Faro is not initialized, so getLogger() falls back to console. Note that
// on a cold page load these run before Root mounts Faro, so they reach the console only.
const logEntityVerification = (stage: string, context: Record<string, unknown>): void => {
  const details = Object.entries(context)
    .map(([key, value]) => `${key}=${String(value)}`)
    .join(' ');
  getLogger().pushLog([`[entity-switch] ${stage} ${details}`]);
};

export const withEmbedded = (path: string): string => {
  const shouldEmbed = isEmbeddedFromUrl();
  if (!shouldEmbed) {
    return path;
  }
  const hasQuery = path.includes('?');
  return `${path}${hasQuery ? '&' : '?'}embedded=true`;
};

const fetchEntityForRequest = async ({
  requestedEntitySubdomain,
  currentEntity,
  userEntitySubdomain
}: {
  requestedEntitySubdomain: string;
  currentEntity: ReturnType<typeof getEntity>;
  userEntitySubdomain: string;
}) => {
  try {
    return await entityEffects.fetchRequestedOrCurrentEntity({
      requestedEntitySubdomain,
      currentEntity,
      surfaceRequestedEntityError: ReportingConfig.IS_PSP_BUILD
    });
  } catch (error) {
    logEntityVerification('requested-entity-fetch-failed', {
      requestedEntitySubdomain,
      userEntitySubdomain,
      status: (error as { status?: number }).status ?? 'unknown'
    });
    throw error;
  }
};

export const verifyRequestedEntity =
  (store: IComposeAppRendererParams<object>['store'], routeLoader: RouteLoader): LoaderFunction<unknown> =>
  async (loaderFunctionArgs) => {
    const state = store.getState();
    const requestedEntitySubdomain = loaderFunctionArgs.params.entitySubdomain ?? '';
    const userEntitySubdomain = getSessionEntitySubdomain(state);
    const currentEntity = getEntity(state);

    // Entity access is authorized by the entity and claims APIs below, never by the legacy
    // v1 `abilities.entity.switch_to` flag. That flag means "is OpenGov staff" and is false
    // for every customer role, so gating on it bounced multi-entity guest users back to
    // their home entity before authorization was ever checked (BUD-18588). The requested
    // subdomain from the URL is only a request: the entity fetch resolves it server-side,
    // and an entity the user cannot read falls back to the current entity and is redirected
    // below. In PSP, surface that failed fetch instead (let it propagate to the route error
    // boundary) rather than masking a real 403/404 as a redirect to the home entity; classic
    // builds keep the silent fallback so a stale bookmark lands on home, not an error page.
    const entity = await fetchEntityForRequest({ requestedEntitySubdomain, currentEntity, userEntitySubdomain });
    await claimsEffects.fetchClaims({});
    if (entity.subdomain !== requestedEntitySubdomain) {
      logEntityVerification('redirect-to-resolved-entity', {
        requestedEntitySubdomain,
        resolvedEntitySubdomain: entity.subdomain,
        userEntitySubdomain
      });
      return redirect(withEmbedded(`/${entity.subdomain}`));
    }

    return routeLoader(store)(loaderFunctionArgs);
  };

interface ITrackingData {
  name: string;
  trackingName?: string;
  trackingProps?: { [key: string]: any };
}

let isFirstLoad = true;
export const trackRoute = ({ name, trackingName, trackingProps = {} }: ITrackingData) => {
  if (isFirstLoad) {
    void SegmentEffects.page({
      pageName: trackingName || name,
      props: trackingProps
    });
    isFirstLoad = false;
  }
};

/**
 * This function is used to navigate to a different route in a hybrid way
 * If react-router's navigate function is provided, it will be used to navigate to provided reactRouter.path
 * else compose-app-router's routerActionCreators will be used
 * @param reactRouter.navigate - react-router's navigate function
 * @param reactRouter.path - path to navigate to using react-router (should not contain "/reporting")
 * @param composeAppRouter.action - action to be dispatched to navigate using compose-app-router ("push" is used by default)
 * @param composeAppRouter.path - path to navigate to using compose-app-router (should contain "/reporting")
 */
export const hybridNavigate = ({
  reactRouter,
  composeAppRouter: { action = routerActionCreators.push, path }
}: {
  reactRouter: {
    navigate?: NavigateFunction;
    path: string;
  };
  composeAppRouter: {
    action?: typeof routerActionCreators.push;
    path: string;
  };
}) => {
  if (typeof reactRouter.navigate === 'function') {
    reactRouter.navigate(reactRouter.path);
  } else {
    action(path);
  }
};

/**
 * This function is used to navigate to a different route by loading/refreshing the page
 * Can be used:
 * - to navigate from react-router to compose-app-router
 * - when state refresh needed
 * @param path - path to navigate to (should contain "/reporting")
 */
export const hardNavigate = (path: string) => {
  window.location.href = path;
};
